import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { slowCn } from '../../lib/utils';
import { stackStyle } from './styles';
import type { BaseStackProps } from './types';

function getParsedGap({ gap }: { gap: string | number | undefined }) {
  if (typeof gap === 'number') return `${(gap * 8).toString()}px`;
  if (typeof gap === 'string') return gap;
  return undefined;
}
function getParsedSpacing({ spacing }: { spacing: string | number | undefined }) {
  if (typeof spacing === 'number') return `${(spacing * 8).toString()}px`;
  if (typeof spacing === 'string') return spacing;
  return undefined;
}
export function Stack<T>({
  children,
  spacing,
  gap,
  className,
  component: Component = 'div',
  direction = 'column',
  alignItems,
  justifyContent = 'flex-start',
  flexWrap,
  alignContent,
  justifyItems,
  style,
  ref,
  ...rest
}: PropsWithChildren<BaseStackProps> & { ref?: React.Ref<T> }) {
  const _className = useMemo(
    () =>
      stackStyle({
        direction,
        alignItems,
        justifyContent,
        flexWrap,
        alignContent,
        justifyItems,
      }),
    [direction, alignItems, justifyContent, flexWrap, alignContent, justifyItems],
  );

  const mergedStyles = useMemo(() => {
    const parsedGap = getParsedGap({ gap });
    const parsedSpacing = getParsedSpacing({ spacing });

    return {
      gap: parsedGap || parsedSpacing,
      ...(style || {}),
    };
  }, [gap, spacing, style]);

  return (
    <Component className={slowCn(_className, className)} ref={ref} {...rest} style={mergedStyles}>
      {children}
    </Component>
  );
}

export default Stack;
Stack.displayName = 'Stack';
